import PersonSearchOutlined from '@mui/icons-material/PersonSearchOutlined';
import Drawer from 'components/Drawer/Drawer';
import useTranslation from 'hooks/useTranslation/useTranslation';
import React, { useMemo } from 'react';
import { BreadcrumbMeta } from 'v2/components/Breadcrumb/Breadcrumb';
import { useDataGridSelectionWithPagination } from 'v2/components/DataGrid/useDataGridSelectionWithPagination';
import EmptyStateOverview from 'v2/components/EmptyStateOverview/EmptyStateOverview';
import useModalContext from 'v2/components/Modal/useModalContext';
import OverviewContent from 'v2/components/OverviewContent/OverviewContent';
import PageTabs from 'v2/components/PageTabs/PageTabs';
import SearchAndFilters from 'v2/components/SearchAndFilters/SearchAndFilters';
import useSnackbarContext from 'v2/components/Snackbar/useSnackbarContext';
import useVisitorsGroupsCache from 'v2/hooks/useVisitorsGroups/useVisitorsGroupsCache';
import SomethingWentWrong from 'v2/pages/GeneralErrors/SomethingWentWrong';
import { createUsersTabs } from 'v2/pages/Users/createUserTabs';
import AddUserGroupModal from './components/AddUserGroupModal';
import GroupsOverviewTable, { GroupsOverviewRow } from './components/GroupsOverviewTable';
import { useUserGroupSearchAndFilters } from './useUserGroupSearchAndFilters';

const UserGroupOverview: React.FC = () => {
  const { translation } = useTranslation();
  const { showSnackbar } = useSnackbarContext();
  const { openModal } = useModalContext();
  const {
    allVisitorGroups,
    loadingAllVisitorGroups: isLoading,
    hasVisitorGroupsFetchError,
    retryFetchingVisitorGroups
  } = useVisitorsGroupsCache({
    handleFetchError: (msg) => showSnackbar(msg, { severity: 'error' })
  });

  const { filterStr, setFilterStr, filteredVisitorGroups, countCaption, clearAllFilters, chips, sections } =
    useUserGroupSearchAndFilters({ allVisitorGroups });

  const allRows = useMemo<GroupsOverviewRow[]>(
    () => allVisitorGroups.map((visitorGroup) => ({ id: visitorGroup.id, visitorGroup })),
    [allVisitorGroups]
  );
  const filteredRows = useMemo<GroupsOverviewRow[]>(
    () => filteredVisitorGroups.map((visitorGroup) => ({ id: visitorGroup.id, visitorGroup })),
    [filteredVisitorGroups]
  );
  // Selected groups stay pinned in the table even when the filters exclude them, so they count as results.
  const selection = useDataGridSelectionWithPagination({ allRows, filteredRows });

  const tabs = createUsersTabs(translation);
  const breadcrumb = {
    title: translation.user_groups,
    crumbs: [
      {
        text: translation.users,
        path: '/users'
      }
    ]
  };

  const handleOpenAddVisitorGroupModal = () => {
    openModal(<AddUserGroupModal />, { maxWidth: 900, width: '80vw' });
  };

  if (hasVisitorGroupsFetchError) {
    return <SomethingWentWrong onRetry={retryFetchingVisitorGroups} />;
  }

  return (
    <>
      <BreadcrumbMeta breadcrumb={breadcrumb} />
      <PageTabs tabs={tabs} />
      <SearchAndFilters
        filterStr={filterStr}
        onFilterStrChange={setFilterStr}
        sections={sections}
        chips={chips}
        countCaption={countCaption}
        actionButton={{
          id: 'add-user-group-button',
          label: translation.add_group,
          onClick: handleOpenAddVisitorGroupModal
        }}
        onClearAll={clearAllFilters}
      />
      <OverviewContent
        loading={isLoading}
        hasData={allVisitorGroups.length > 0}
        hasResults={selection.rows.length > 0}
        table={<GroupsOverviewTable selection={selection} loading={isLoading} />}
        noResults={
          <EmptyStateOverview
            icon={PersonSearchOutlined}
            title={translation.no_results}
            content={translation.no_results_search_and_filters_description}
          />
        }
        noData={
          <EmptyStateOverview
            icon={PersonSearchOutlined}
            title={translation.no_groups_created}
            content={translation.no_groups_added}
            button={{ label: translation.add_group, onClick: handleOpenAddVisitorGroupModal }}
          />
        }
      />
      <Drawer />
    </>
  );
};
export default UserGroupOverview;
